import axios from 'axios';
import { useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { ContentCard } from '../../components/ContentCard/ContentCard';
import { Button } from '../../components/Form/Button';
import { Icon, close, thumbs_up } from '../../components/Icon/Icon';
import { Main } from '../../components/Main/Main';
import { Modal } from '../../components/Modal/Modal';
import { Table } from '../../components/Table/Table';
import { WITH_CREDENTIALS, renderTemplate } from '../../components/utils';
import { useConfig } from '../../config';
import { useImmer } from '../../hooks/useImmer';
import { useTranslation } from '../../i18n/i18n';
import { useApi } from '../../state/api';
import { Res } from '../../state/api-resources';
import { useSession } from '../../state/session';
import { ApiActionResponse, ApiSubAccount } from '../../types';
import { Routing } from '../Routes';
import { ErrorPage } from './ErrorPage';
import { LoaderPage } from './LoaderPage';
import './SwitchAccountPage.css';

export const SwitchAccount = (): JSX.Element => {
	const [accounts, setAccounts] = useImmer<ApiSubAccount[] | undefined>([]);
	const { api } = useConfig();
	const { user } = useSession();
	const { data, error } = useApi(Res.subAccounts, []);
	const t = { ...useTranslation().switchAccount, urls: useTranslation().routes, form: useTranslation().form };
	const tAccount = useTranslation().accountPage;
	const history = useHistory();
	const locationState = useLocation<{ logInAndReload: boolean; loggedIn: boolean }>().state;

	const resetUpdatedState = () => {
		if (locationState) {
			locationState.logInAndReload = false;
			locationState.loggedIn = false;
			history.replace({ ...history.location });
		}
	};

	useEffect(() => {
		if (locationState && locationState.logInAndReload === true) {
			locationState.logInAndReload = false;
			locationState.loggedIn = true;
			history.replace({ ...history.location });
		}
	}, []);

	useEffect(() => {
		return setAccounts(data);
	}, [data]);

	if (error) return <ErrorPage message={error.message} />;
	if (!accounts) return <LoaderPage />;

	return (
		<Main>
			<h1 className="big">{t.switchAccount}</h1>
			<div className="sub-accounts">
				<Table className="not-mobile" striped>
					<thead>
						<tr>
							<th>{tAccount.companyName}</th>
							<th>{t.address}</th>
							<th>{t.city}</th>
							<th></th>
						</tr>
					</thead>
					<tbody>
						{Boolean(accounts.length) &&
							accounts.map((account) => {
								const { id, company, email, deliveryAddress } = account;

								return (
									<tr key={id}>
										<td>{company}</td>
										<td>{deliveryAddress?.street ? deliveryAddress.street : email}</td>
										<td>
											{deliveryAddress.zip} {deliveryAddress.city}
										</td>
										<td>
											{user?.id === id ? (
												<Button className="text" disabled>
													{t.loggedIn}
												</Button>
											) : (
												<Button
													onClick={async () => {
														const { data } = await axios.post<ApiActionResponse>(
															`${api}me/login/${id}.json`,
															null,
															WITH_CREDENTIALS
														);

														if (data.success) {
															history.push({
																pathname: t.urls.switchAccount,
																state: { logInAndReload: true, loggedIn: false },
															});
															window.location.reload();
														}
													}}
												>
													{t.login}
												</Button>
											)}
										</td>
									</tr>
								);
							})}
					</tbody>
				</Table>
			</div>

			{locationState?.loggedIn && user && (
				<Modal className="logged-in-modal" show={locationState?.loggedIn} onDismiss={() => ({})}>
					<ContentCard className="logged-in-content">
						<button className="close-btn" onClick={resetUpdatedState}>
							<Icon shape={{ ...close, fill: 'black' }} size={20} />
						</button>

						<div className="logged-in-content-main">
							<span className="logged-in-icon">
								<Icon shape={thumbs_up} size={44} />
							</span>
							<p className="logged-in-title">
								{renderTemplate(t.loggedInAs, { firstname: user.firstname, lastname: user.lastname })}
							</p>
							<div>
								<Button onClick={resetUpdatedState}>{t.ok}</Button>
							</div>
						</div>
					</ContentCard>
				</Modal>
			)}
		</Main>
	);
};

export const switchAccountRouting: Routing = {
	path(urls) {
		return urls.switchAccount;
	},
	component: SwitchAccount,
	requiresAuth: true,
};
